import { useCallback, useEffect, useState } from 'react'
import { admin, REQUEST_LABEL, type PageRequestItem, type RequestState } from '../../api/client'

/*
  The queue, ported from the content queue on cpCenter2.

  Grouped by the page each request was left on, because that is how the work gets done:
  whoever is applying them opens one page and clears everything on it, rather than jumping
  between four files to service a flat list sorted by time.
*/

const STATES: RequestState[] = ['pending', 'doing', 'done', 'skip']

export function Requests() {
  const [data, setData] = useState<{ items: PageRequestItem[]; counts: Record<string, number> } | null>(null)
  const [filter, setFilter] = useState<RequestState | ''>('')
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    setData(await admin.requests(filter || undefined))
  }, [filter])

  useEffect(() => { void load() }, [load])

  const act = async (id: string, run: () => Promise<unknown>) => {
    if (busy) return
    setBusy(id)
    try { await run(); await load() } finally { setBusy(null) }
  }

  const byPage = new Map<string, PageRequestItem[]>()
  for (const item of data?.items ?? []) {
    const list = byPage.get(item.page) ?? []
    list.push(item)
    byPage.set(item.page, list)
  }

  return (
    <>
      <div className="ad__head">
        <div>
          <h1>Page requests</h1>
          <p className="ad__sub">
            Change requests left on the live site. Work the pending ones.
          </p>
        </div>
        <a className="ad__btn" href="/" target="_blank" rel="noreferrer">Browse the site</a>
      </div>

      <div className="rq-stats">
        {STATES.map(s => (
          <div className="rq-stat" key={s}>
            <span className={`rq-stat-n is-${s}`}>{data?.counts[s] ?? 0}</span>
            <span className="rq-stat-l">{REQUEST_LABEL[s]}</span>
          </div>
        ))}
      </div>

      <div className="ad__tabs" role="tablist">
        <button
          role="tab" className="ad__tab" aria-selected={filter === ''}
          onClick={() => setFilter('')}
        >
          All
        </button>
        {STATES.map(s => (
          <button
            key={s} role="tab" className="ad__tab" aria-selected={filter === s}
            onClick={() => setFilter(s)}
          >
            {REQUEST_LABEL[s]}
          </button>
        ))}
      </div>

      {!data ? (
        <p className="ad__muted">Loading</p>
      ) : byPage.size === 0 ? (
        <div className="ad__empty">
          <p>Nothing here.</p>
          <p className="ad__hint">
            Open the site while signed in here and use the button in the corner to leave one.
          </p>
        </div>
      ) : (
        [...byPage.entries()].map(([page, items]) => (
          <section className="rq-group" key={page}>
            <h2 className="rq-group-head">
              <code>{page}</code>
              <a href={page} target="_blank" rel="noreferrer">Open page</a>
            </h2>

            {items.map(item => (
              <article className={`rq-card is-${item.status}`} key={item.id}>
                <div className="rq-card-top">
                  <span className="rq-badge">{item.kind}</span>
                  <span className={`rq-badge rq-badge--${item.status}`}>{REQUEST_LABEL[item.status]}</span>
                  <time>{new Date(item.createdAt).toLocaleString()}</time>
                </div>

                <p className="rq-card-body">{item.body}</p>

                {item.shot && (
                  <a href={`/api/admin/requests/${item.id}/shot`} target="_blank" rel="noreferrer">
                    <img className="rq-card-shot" src={`/api/admin/requests/${item.id}/shot`} alt="" />
                  </a>
                )}

                {item.target && <code className="rq-card-target">{item.target}</code>}

                <div className="rq-card-actions">
                  {STATES.map(s => (
                    <button
                      key={s}
                      className="ad__btn ad__btn--tiny"
                      disabled={busy === item.id || item.status === s}
                      onClick={() => act(item.id, () => admin.setRequestStatus(item.id, s))}
                    >
                      {REQUEST_LABEL[s]}
                    </button>
                  ))}
                  <button
                    className="ad__btn ad__btn--tiny ad__btn--danger"
                    disabled={busy === item.id}
                    onClick={() => {
                      if (window.confirm('Remove this request?')) {
                        void act(item.id, () => admin.removeRequest(item.id))
                      }
                    }}
                  >
                    Remove
                  </button>
                </div>
              </article>
            ))}
          </section>
        ))
      )}
    </>
  )
}
